import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Typography,
  Divider,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  NcrDetails,
  NcrCategoryEnum,
  NcrSubCategoryEnum,
  NcrSeverityEnum,
  NcrImpactAreaEnum,
  NcrDetectionMethodEnum,
  NcrRootCauseCategoryEnum,
  NcrStatusEnum,
  NcrVerificationMethodEnum,
  RaidCategoryEnum,
  RaidItemCreate,
  User,
  getUsers,
} from '../../services/api';
import {
  getDefaultExpectedDate,
  getIsoDate,
  PartialRaidCommonProps,
} from './raidFormUtils';

interface NcrDialogProps extends PartialRaidCommonProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RaidItemCreate) => void;
  initialData?: NcrDetails | null;
  itemId?: number | null;
  title?: string;
  readOnly?: boolean;
  isFromOtherQuestionnaire?: boolean;
  ncrOptions?: { raid_id: number; title: string }[];
}

const emptyNcrForm = (): NcrDetails => ({
  description: '',
  category: undefined,
  sub_category: undefined,
  requirement_violated: '',
  source_of_requirement: '',
  severity: NcrSeverityEnum.MINOR,
  business_impact: '',
  impact_area: undefined,
  detected_on: '',
  detected_by: '',
  detection_method: undefined,
  occurrence_date: '',
  root_cause: '',
  root_cause_category: undefined,
  immediate_correction: '',
  corrective_action: '',
  preventive_action: '',
  person_responsible: '',
  target_closure_date: '',
  actual_closure_date: '',
  status: NcrStatusEnum.OPEN,
  verification_method: undefined,
  verified_by: '',
  evidence: '',
  related_risks: '',
  related_issues: '',
  related_change_requests: '',
  repeat_occurrence: false,
  previous_ncr_reference: '',
  lessons_learned: '',
  management_comments: '',
});

const NcrDialog: React.FC<NcrDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  itemId,
  title = '',
  readOnly = false,
  isFromOtherQuestionnaire = false,
  defaultAssigneeId,
  initialPartialRaid = false,
  ncrOptions,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [partialRaid, setPartialRaid] = useState(false);
  // Partial RAID applies only on initial create, never when editing an existing item
  const isCreating = !itemId;
  const effectivePartialRaid = isCreating && partialRaid;
  const [formData, setFormData] = useState<NcrDetails>(emptyNcrForm());
  const [formTitle, setFormTitle] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        const filteredUsers = response.data.filter(
          (user: User) =>
            user.display_name !== 'System User' &&
            user.user_name !== 'system_user' &&
            user.status === 'ACTIVE'
        );
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  const findUserById = (userId: string): User | null => {
    if (!userId) return null;
    return users.find((u) => u.id.toString() === userId) || null;
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...emptyNcrForm(),
        ...initialData,
        severity: initialData.severity || NcrSeverityEnum.MINOR,
        status: initialData.status || NcrStatusEnum.OPEN,
        repeat_occurrence: !!initialData.repeat_occurrence,
      });
    } else {
      const today = getIsoDate();
      setFormData({
        ...emptyNcrForm(),
        detected_on: today,
        occurrence_date: today,
      });
    }

    setFormTitle(title || '');
    setPartialRaid(itemId ? false : !!initialPartialRaid);
    setErrors({});
  }, [initialData, title, initialPartialRaid, open, itemId]);

  const applyPartialDefaults = () => {
    setFormData((prev) => ({
      ...prev,
      person_responsible:
        prev.person_responsible ||
        (defaultAssigneeId != null ? String(defaultAssigneeId) : ''),
      severity: prev.severity || NcrSeverityEnum.MINOR,
      target_closure_date:
        prev.target_closure_date || getDefaultExpectedDate(),
    }));
  };

  const handlePartialRaidChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const enabled = event.target.checked;
    setPartialRaid(enabled);
    if (enabled && !itemId && !initialData) {
      applyPartialDefaults();
    }
  };

  const handleTextChange =
    (field: keyof NcrDetails) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    };

  const handleSelectChange =
    (field: keyof NcrDetails) =>
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormTitle(event.target.value);
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formTitle.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.person_responsible?.trim()) {
      newErrors.person_responsible = 'Action Owner is required';
    }

    if (!effectivePartialRaid) {
      if (!formData.category) {
        newErrors.category = 'Category is required';
      }
      if (!formData.severity) {
        newErrors.severity = 'Severity is required';
      }
      if (!formData.impact_area) {
        newErrors.impact_area = 'Impact area is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const details: NcrDetails = {
        ...formData,
        detected_on: formData.detected_on || undefined,
        occurrence_date: formData.occurrence_date || undefined,
        target_closure_date: formData.target_closure_date || undefined,
        actual_closure_date: formData.actual_closure_date || undefined,
      };

      if (effectivePartialRaid) {
        details.severity = details.severity || NcrSeverityEnum.MINOR;
        details.target_closure_date =
          details.target_closure_date || getDefaultExpectedDate();
      }

      const submitData: RaidItemCreate = {
        entity_id: 0,
        title: formTitle,
        raid_category: RaidCategoryEnum.NCR,
        partial_raid: effectivePartialRaid,
        details,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting NCR item:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedPreviousNcr =
    ncrOptions?.find(
      (o) => String(o.raid_id) === String(formData.previous_ncr_reference)
    ) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly ? 'View NCR' : itemId ? 'Update NCR' : 'Add New NCR'}
        {loading && (
          <CircularProgress
            size={20}
            sx={{ marginLeft: 2, verticalAlign: 'middle' }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {readOnly && (
          <Typography color="info.main" variant="subtitle2" sx={{ mb: 2 }}>
            {isFromOtherQuestionnaire
              ? 'This item is from other reviews and is displayed in read-only mode.'
              : 'This review is submitted. RAID details are in read-only mode.'}
          </Typography>
        )}

        {isCreating && (
        <FormControlLabel
          control={
            <Switch
              checked={partialRaid}
              onChange={handlePartialRaidChange}
              disabled={readOnly}
            />
          }
          label="Partial RAID (show only required fields)"
          sx={{ mb: 1 }}
        />
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              value={formTitle}
              onChange={handleTitleChange}
              fullWidth
              required
              error={!!errors.title}
              helperText={
                errors.title || 'Short summary of the nonconformance'
              }
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleTextChange('description')}
              multiline
              rows={3}
              fullWidth
              required
              error={!!errors.description}
              helperText={
                errors.description ||
                'Detailed description of what occurred'
              }
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          {!effectivePartialRaid && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  disabled={readOnly}
                  required={!effectivePartialRaid}
                  error={!!errors.category}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category || ''}
                    label="Category"
                    onChange={handleSelectChange('category') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(NcrCategoryEnum).map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Sub-Category</InputLabel>
                  <Select
                    value={formData.sub_category || ''}
                    label="Sub-Category"
                    onChange={handleSelectChange('sub_category') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(NcrSubCategoryEnum).map((sub) => (
                      <MenuItem key={sub} value={sub}>
                        {sub}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Requirement / Standard Violated"
                  value={formData.requirement_violated || ''}
                  onChange={handleTextChange('requirement_violated')}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Source of Requirement"
                  value={formData.source_of_requirement || ''}
                  onChange={handleTextChange('source_of_requirement')}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              disabled={readOnly}
              required={!effectivePartialRaid}
              error={!!errors.severity}
            >
              <InputLabel>Severity</InputLabel>
              <Select
                value={formData.severity || ''}
                label="Severity"
                onChange={handleSelectChange('severity') as any}
                readOnly={readOnly}
                inputProps={{ readOnly }}
              >
                {Object.values(NcrSeverityEnum).map((sev) => (
                  <MenuItem key={sev} value={sev}>
                    {sev}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {!effectivePartialRaid && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  disabled={readOnly}
                  required={!effectivePartialRaid}
                  error={!!errors.impact_area}
                >
                  <InputLabel>Impact Area</InputLabel>
                  <Select
                    value={formData.impact_area || ''}
                    label="Impact Area"
                    onChange={handleSelectChange('impact_area') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(NcrImpactAreaEnum).map((area) => (
                      <MenuItem key={area} value={area}>
                        {area}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Business Impact"
                  value={formData.business_impact || ''}
                  onChange={handleTextChange('business_impact')}
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  Detection
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Detected On"
                  type="date"
                  value={formData.detected_on || ''}
                  onChange={handleTextChange('detected_on')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option.display_name}
                  value={findUserById(formData.detected_by || '')}
                  onChange={(_event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      detected_by: newValue ? newValue.id.toString() : '',
                    }));
                  }}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField {...params} label="Detected By" />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No users found"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Detection Method</InputLabel>
                  <Select
                    value={formData.detection_method || ''}
                    label="Detection Method"
                    onChange={handleSelectChange('detection_method') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(NcrDetectionMethodEnum).map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Occurrence Date"
                  type="date"
                  value={formData.occurrence_date || ''}
                  onChange={handleTextChange('occurrence_date')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  Root Cause & Actions
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Root Cause"
                  value={formData.root_cause || ''}
                  onChange={handleTextChange('root_cause')}
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Root Cause Category</InputLabel>
                  <Select
                    value={formData.root_cause_category || ''}
                    label="Root Cause Category"
                    onChange={handleSelectChange('root_cause_category') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(NcrRootCauseCategoryEnum).map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Immediate Correction (Containment)"
                  value={formData.immediate_correction || ''}
                  onChange={handleTextChange('immediate_correction')}
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Corrective Action"
                  value={formData.corrective_action || ''}
                  onChange={handleTextChange('corrective_action')}
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Preventive Action"
                  value={formData.preventive_action || ''}
                  onChange={handleTextChange('preventive_action')}
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  Ownership & Closure
                </Typography>
                <Divider />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.display_name}
              value={findUserById(formData.person_responsible || '')}
              onChange={(_event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  person_responsible: newValue ? newValue.id.toString() : '',
                }));
                if (errors.person_responsible) {
                  setErrors((prev) => ({ ...prev, person_responsible: '' }));
                }
              }}
              disabled={readOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Action Owner"
                  required
                  error={!!errors.person_responsible}
                  helperText={
                    errors.person_responsible ||
                    'Person accountable for closure'
                  }
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No users found"
            />
          </Grid>

          {!effectivePartialRaid && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || NcrStatusEnum.OPEN}
                  label="Status"
                  onChange={handleSelectChange('status') as any}
                  readOnly={readOnly}
                  inputProps={{ readOnly }}
                >
                  {Object.values(NcrStatusEnum).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              label="Target Closure Date"
              type="date"
              value={formData.target_closure_date || ''}
              onChange={handleTextChange('target_closure_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          {!effectivePartialRaid && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Actual Closure Date"
                  type="date"
                  value={formData.actual_closure_date || ''}
                  onChange={handleTextChange('actual_closure_date')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Verification Method</InputLabel>
                  <Select
                    value={formData.verification_method || ''}
                    label="Verification Method"
                    onChange={handleSelectChange('verification_method') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(NcrVerificationMethodEnum).map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option.display_name}
                  value={findUserById(formData.verified_by || '')}
                  onChange={(_event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      verified_by: newValue ? newValue.id.toString() : '',
                    }));
                  }}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField {...params} label="Verified By" />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No users found"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Evidence / Attachments"
                  value={formData.evidence || ''}
                  onChange={handleTextChange('evidence')}
                  fullWidth
                  helperText="Supporting documents or links"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  Related Items & Learning
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Related Risk(s)"
                  value={formData.related_risks || ''}
                  onChange={handleTextChange('related_risks')}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Related Issue(s)"
                  value={formData.related_issues || ''}
                  onChange={handleTextChange('related_issues')}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Related Change Request(s)"
                  value={formData.related_change_requests || ''}
                  onChange={handleTextChange('related_change_requests')}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Repeat Occurrence</InputLabel>
                  <Select
                    value={formData.repeat_occurrence ? 'Yes' : 'No'}
                    label="Repeat Occurrence"
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        repeat_occurrence: e.target.value === 'Yes',
                      }));
                    }}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                {ncrOptions && ncrOptions.length > 0 ? (
                  <Autocomplete
                    options={ncrOptions}
                    getOptionLabel={(option) =>
                      `${option.raid_id}: ${option.title}`
                    }
                    value={selectedPreviousNcr}
                    onChange={(_event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        previous_ncr_reference: newValue
                          ? String(newValue.raid_id)
                          : '',
                      }));
                    }}
                    disabled={readOnly}
                    renderInput={(params) => (
                      <TextField {...params} label="Previous NCR Reference" />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.raid_id === value.raid_id
                    }
                    noOptionsText="No NCRs found"
                  />
                ) : (
                  <TextField
                    label="Previous NCR Reference"
                    value={formData.previous_ncr_reference || ''}
                    onChange={handleTextChange('previous_ncr_reference')}
                    fullWidth
                    InputProps={{ readOnly }}
                    disabled={readOnly}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Lessons Learned"
                  value={formData.lessons_learned || ''}
                  onChange={handleTextChange('lessons_learned')}
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Management Comments"
                  value={formData.management_comments || ''}
                  onChange={handleTextChange('management_comments')}
                  multiline
                  rows={2}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {itemId ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NcrDialog;
